import type { CancelSurveyReason } from '../../services/api';
import type { TrialStatus } from '../../context/TrialContext';
import type {
  BillingCycleOption,
  CancelOption,
  PlanTier,
  RetentionOfferType,
  UserProfile,
} from './profileTypes';

export const RETENTION_OFFER_I18N_KEY: Record<RetentionOfferType, string> = {
  first_month_discount: 'firstMonthDiscount',
  stay20: 'stay20',
  pause: 'pause',
};

/**
 * Copy key for an offer. STAY20 on an MC pack (tier3: 3- or 6-month cycle)
 * discounts exactly the next renewal, not the next 3 months (BIL-L13).
 */
export const retentionOfferI18nKey = (
  offerType: RetentionOfferType,
  planTier?: string | null,
): string =>
  offerType === 'stay20' && planTier && normalizePlanTier(planTier) === 'tier3'
    ? 'stay20Pack'
    : RETENTION_OFFER_I18N_KEY[offerType];

export const PLAN_ALLOWED_BILLING_CYCLES: Record<PlanTier, BillingCycleOption[]> = {
  tier1: ['1mo'],
  tier2: ['1mo'],
  tier3: ['3mo', '6mo'],
};

export const defaultBillingCycleForTier = (tier: PlanTier): BillingCycleOption =>
  PLAN_ALLOWED_BILLING_CYCLES[tier][0] ?? '1mo';

export const normalizeBillingCycleForTier = (
  tier: PlanTier,
  cycle?: BillingCycleOption | string | null,
): BillingCycleOption => {
  const normalized = (cycle || '').toLowerCase();
  const candidate: BillingCycleOption =
    normalized === '6mo' || normalized === 'yearly'
      ? '6mo'
      : normalized === '3mo'
        ? '3mo'
        : '1mo';
  return PLAN_ALLOWED_BILLING_CYCLES[tier].includes(candidate)
    ? candidate
    : defaultBillingCycleForTier(tier);
};

export const normalizePlanTier = (value?: string | null): PlanTier => {
  if (!value) return 'tier2';
  const normalized = value.toLowerCase();
  if (normalized === 'tier1' || normalized === 'individual') return 'tier1';
  if (normalized === 'tier3' || normalized === 'professional') return 'tier3';
  return 'tier2';
};

export const normalizeBillingCycle = (value?: string | null): BillingCycleOption => {
  if (!value) return '1mo';
  const v = value.toLowerCase();
  if (v === '3mo') return '3mo';
  if (v === '6mo') return '6mo';
  // Legacy values from old API
  if (v === 'yearly') return '6mo';
  return '1mo';
};

export const backendDetailText = (error: any): string => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail.message === 'string') return detail.message;
  return '';
};

// Retention-offer endpoints report typed errors the same way billing-checkout
// conflicts do: either `data.code` or `data.detail.code` (see
// getBillingCheckoutConflict in services/api.ts). Falls back to the raw
// string detail, then to an http_<status> placeholder.
export const retentionOfferErrorCode = (error: any): string => {
  const data = error?.response?.data;
  const code = data?.code || data?.detail?.code;
  if (typeof code === 'string') return code;
  if (typeof data?.detail === 'string') return data.detail;
  const status = error?.response?.status;
  return status ? `http_${status}` : 'unknown';
};

export const TERMINAL_SUBSCRIPTION_STATUSES = new Set([
  'canceled',
  'incomplete_expired',
  'subscription_not_found',
  'none',
]);

// Segment + reason driven offer sequence. Trial users see the first-month
// discount regardless of reason (no trial extension: it conflicted with the
// 3-day trial, BIL-R16) — active paid
// users see exactly one offer chosen by reason. Users already scheduled to
// cancel at period end have already been through this once, so they skip
// straight to confirm. A paused subscription (BIL-L18) has no offer left:
// STAY20 needs an active subscription and the pause is already used.
export const computeOfferQueue = (
  cancelAtPeriodEnd: boolean | undefined,
  reason: CancelSurveyReason | null,
  isTrial: boolean,
  isPaused = false,
): RetentionOfferType[] => {
  if (cancelAtPeriodEnd || isPaused) return [];
  if (isTrial) return ['first_month_discount'];
  if (reason === 'too_expensive') return ['stay20'];
  if (reason === 'not_using' || reason === 'temporary_break') return ['pause'];
  return ['stay20'];
};

/**
 * Cancel timings offered in the confirm step. Paid subscribers cancel at the
 * end of the period only (BIL-L12); immediate termination is a support/refund
 * action. A card-backed trial can end now or at trial end, neither charges (BIL-L15).
 */
export const cancelOptionIds = (isCardBackedTrial: boolean): CancelOption[] =>
  isCardBackedTrial ? ['period_end', 'immediate'] : ['period_end'];

export interface SubscriptionFlags {
  canManagePlan: boolean;
  subscriptionStatus: string;
  isPaidSubscriptionActive: boolean;
  /** Stripe-paused (BIL-L18): no paid access; Resume starts a new paid period. */
  isPaused: boolean;
  isTrialActive: boolean;
  isCardBackedTrial: boolean;
  isNoCardTrial: boolean;
  shouldResubscribe: boolean;
  hasStripeBilling: boolean;
  canCancelBilling: boolean;
  canChangePaidPlan: boolean;
  shouldChoosePlan: boolean;
}

type SubscriptionFlagsProfile = {
  user_type?: UserProfile['user_type'];
  subscription?: Partial<Pick<UserProfile['subscription'], 'status' | 'cancel_at_period_end'>>;
};

type SubscriptionFlagsTrial = Pick<TrialStatus, 'active' | 'kind'>;

/** Which billing actions the profile screen offers for this account. */
export const deriveSubscriptionFlags = (
  profile: SubscriptionFlagsProfile | null | undefined,
  trialStatus: SubscriptionFlagsTrial | null | undefined,
): SubscriptionFlags => {
  const normalizedUserType = profile?.user_type ? profile.user_type.toLowerCase() : '';
  const canManagePlan = normalizedUserType === 'b2c';
  const subscriptionStatus = (profile?.subscription?.status || '').toLowerCase();
  const isPaidSubscriptionActive = subscriptionStatus === 'active';
  const isPaused = subscriptionStatus === 'paused';
  const isTrialActive = trialStatus?.active === true;
  const isCardBackedTrial = subscriptionStatus === 'trialing'
    || (isTrialActive && trialStatus?.kind === 'card_backed');
  const isNoCardTrial = isTrialActive && !isCardBackedTrial;
  const shouldResubscribe = canManagePlan
    && TERMINAL_SUBSCRIPTION_STATUSES.has(subscriptionStatus || 'none');
  const hasStripeBilling = canManagePlan && (
    isCardBackedTrial
    || isPaidSubscriptionActive
    || ['past_due', 'unpaid', 'incomplete', 'paused', 'trialing'].includes(subscriptionStatus)
  );
  // A paused subscription can be cancelled too: it ends now, no charge (BIL-L18 (4)).
  const canCancelBilling = canManagePlan
    && (isCardBackedTrial || isPaidSubscriptionActive || isPaused || subscriptionStatus === 'trialing')
    && !profile?.subscription?.cancel_at_period_end;
  const canChangePaidPlan = canManagePlan && isPaidSubscriptionActive && !isTrialActive;
  const shouldChoosePlan = canManagePlan
    && !shouldResubscribe
    && !isCardBackedTrial
    && !isPaidSubscriptionActive
    && !isPaused;
  return {
    canManagePlan,
    subscriptionStatus,
    isPaidSubscriptionActive,
    isPaused,
    isTrialActive,
    isCardBackedTrial,
    isNoCardTrial,
    shouldResubscribe,
    hasStripeBilling,
    canCancelBilling,
    canChangePaidPlan,
    shouldChoosePlan,
  };
};

export type AccountSecurityAction = 'billingPortal' | 'cancel' | 'resubscribe' | 'choosePlan';

/**
 * Rows of the "Account & security" panel. They are billing shortcuts only (the
 * password row went with passwordless sign-in), and on compact layouts the plan
 * card shows the same buttons, so compact gets none. An empty list means the
 * panel is not rendered at all: it used to show its title over a blank card.
 */
export const accountSecurityActions = (
  flags: Pick<SubscriptionFlags, 'hasStripeBilling' | 'canCancelBilling' | 'shouldResubscribe' | 'shouldChoosePlan'>,
  isCompact: boolean,
): AccountSecurityAction[] => {
  if (isCompact) return [];
  const actions: AccountSecurityAction[] = [];
  if (flags.hasStripeBilling) actions.push('billingPortal');
  if (flags.canCancelBilling) actions.push('cancel');
  if (flags.shouldResubscribe) actions.push('resubscribe');
  if (flags.shouldChoosePlan) actions.push('choosePlan');
  return actions;
};
